import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { API_BASE_URL } from '../../../constants/api-urls';
import '../../../assets/styles/purchase_order_accept.scss';
export default function PurchaseOrderAccept() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currencyOptions } = useCurrencyMaster();
  
  const [loading, setLoading] = useState(true);
  const [poData, setPoData] = useState(null);
  const [quotationData, setQuotationData] = useState(null);
  const [rfqData, setRfqData] = useState(null);
  const [buyerDetails, setBuyerDetails] = useState(null);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [deliverySchedules, setDeliverySchedules] = useState([]);
  const [termsConditions, setTermsConditions] = useState([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get poId from location.state
      const poIdFromState = location.state?.poId;
      if (!poIdFromState) {
        toast.error('Purchase Order ID not found');
        navigate('/seller-purchase-order-list');
        return;
      }

      // Parse poId
      let poId = poIdFromState;
      if (typeof poIdFromState === 'string' && poIdFromState.startsWith('PO-')) {
        const match = poIdFromState.match(/(\d+)$/);
        if (match && match[1]) {
          poId = parseInt(match[1], 10);
        } else {
          toast.error('Invalid PO ID format');
          navigate('/seller-purchase-order-list');
          return;
        }
      } else if (typeof poIdFromState === 'string') {
        poId = parseInt(poIdFromState, 10);
        if (isNaN(poId)) {
          toast.error('Invalid PO ID');
          navigate('/seller-purchase-order-list');
          return;
        }
      }

      // Fetch PO data
      const poResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (!poResponse || !poResponse.success || !poResponse.data) {
        toast.error('Failed to load purchase order data');
        navigate('/seller-purchase-order-list');
        return;
      }

      const po = poResponse.data;
      setPoData(po);

      const sellerCanAccept = po.seller_can_accept !== false;
      const approvalPending =
        po.seller_approval_pending === true || po.seller_approval_status === 'pending';
      if (approvalPending || !sellerCanAccept) {
        toast.error('This purchase order requires internal approval before it can be accepted.');
        navigate('/seller-purchase-order-list');
        return;
      }

      // Extract and set form data
      const poDataObj = po.po_data || {};
      setFormData({
        poNumber: po.po_number || 'N/A',
        poDate: po.po_date || '',
        expectedDeliveryDate: po.expected_delivery_date || poDataObj.expectedDeliveryDate || '',
        priorityLevel: po.priority_level || poDataObj.priorityLevel || 'Normal',
        paymentTerms: po.payment_terms || poDataObj.paymentTerms || '',
        freightTerms: po.freight_terms || poDataObj.freightTerms || '',
        deliveryTerms: po.delivery_terms || poDataObj.deliveryTerms || '',
        tradeScenario: po.trade_scenario || poDataObj.tradeScenario || '',
        tradeType: po.trade_type || poDataObj.tradeType || '',
        tradeLocation: po.trade_location || poDataObj.tradeLocation || '',
        buyerAddress: po.buyer_company_address || poDataObj.buyerAddress || '',
        buyerGstNumber: po.buyer_company_gst_number || po.buyer_gst_number || poDataObj.buyerGstNumber || '',
        buyerState: po.buyer_state || poDataObj.buyerState || '',
        buyerCountry: po.buyer_country || poDataObj.buyerCountry || ''
      });

      // Set line items
      const items = po.line_items || poDataObj.lineItems || [];
      setLineItems(items);

      // Set delivery schedules
      const schedules = po.delivery_schedules || poDataObj.deliverySchedules || [];
      setDeliverySchedules(Array.isArray(schedules) ? schedules : []);

      // Set terms and conditions
      const terms = po.terms_conditions || poDataObj.termsConditions || [];
      setTermsConditions(Array.isArray(terms) ? terms : []);

      // Set special instructions
      setSpecialInstructions(po.special_instructions || poDataObj.specialInstructions || '');

      // Set attachments
      const poAttachments = po.attachments || poDataObj.attachments || [];
      setAttachments(Array.isArray(poAttachments) ? poAttachments : []);

      // Parse buyer address from PO data
      let buyerAddress = '';
      if (po.buyer_company_address) {
        try {
          // Try to parse as JSON if it's a string
          const addressData = typeof po.buyer_company_address === 'string' 
            ? (po.buyer_company_address.startsWith('{') ? JSON.parse(po.buyer_company_address) : po.buyer_company_address)
            : po.buyer_company_address;
          
          if (typeof addressData === 'object' && addressData.address) {
            const addressParts = [
              addressData.address,
              addressData.landmark,
              addressData.city,
              addressData.state,
              addressData.country,
              addressData.pincode
            ].filter(Boolean);
            buyerAddress = addressParts.join(', ');
          } else if (typeof addressData === 'string') {
            buyerAddress = addressData;
          }
        } catch (e) {
          // If parsing fails, use as string
          buyerAddress = typeof po.buyer_company_address === 'string' ? po.buyer_company_address : '';
        }
      }

      // Set buyer details initially
      setBuyerDetails({
        companyName: po.buyer_company_name || 'N/A',
        address: buyerAddress,
        gstNumber: po.buyer_company_gst_number || po.buyer_gst_number || '',
        state: po.buyer_state || '',
        country: po.buyer_country || '',
        contactPerson: '',
        email: '',
        phone: ''
      });

      // Set seller details
      setSellerDetails({
        companyName: po.seller_company_name || 'N/A',
        address: po.seller_company_address || po.seller_address || '',
        gstNumber: po.seller_company_gst_number || po.seller_gst_number || '',
        state: po.seller_state || '',
        country: po.seller_country || '',
        contactPerson: '',
        email: '',
        phone: ''
      });

      // Fetch quotation data if quotation_id exists
      let fetchedQuotation = null;
      let fetchedRfqData = null;
      if (po.quotation_id) {
        try {
          const quotationResponse = await CommonService.get(`/quotation/${po.quotation_id}`);
          if (quotationResponse && quotationResponse.success && quotationResponse.data) {
            fetchedQuotation = quotationResponse.data;
            setQuotationData(fetchedQuotation);
            
            // Fetch RFQ data if rfq_id exists
            if (fetchedQuotation.rfq_id) {
              try {
                const rfqResponse = await CommonService.get(`/rfq/${fetchedQuotation.rfq_id}`);
                if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                  fetchedRfqData = rfqResponse.data;
                  setRfqData(fetchedRfqData);
                }
              } catch (rfqError) {
                console.error('Error fetching RFQ:', rfqError);
              }
            }
          }
        } catch (quotationError) {
          console.error('Error fetching quotation:', quotationError);
        }
      }

      // Fetch buyer company profile for contact details
      if (po.buyer_company_id) {
        try {
          const buyerProfileResponse = await CommonService.get(`/company/profile/${po.buyer_company_id}`);
          if (buyerProfileResponse && buyerProfileResponse.success && buyerProfileResponse.data) {
            const buyerProfile = buyerProfileResponse.data;
            const contactDetails = buyerProfile.contact_details || {};
            const addressObj = buyerProfile.address || {};
            
            // Format address from address object
            let formattedAddress = '';
            if (typeof addressObj === 'object' && addressObj.address) {
              const addressParts = [
                addressObj.address,
                addressObj.landmark,
                addressObj.city,
                addressObj.state,
                addressObj.country,
                addressObj.pincode
              ].filter(Boolean);
              formattedAddress = addressParts.join(', ');
            } else if (typeof addressObj === 'string') {
              formattedAddress = addressObj;
            } else if (po.buyer_company_address) {
              formattedAddress = po.buyer_company_address;
            }
            
            // Try to get email and phone from multiple sources
            let buyerEmail = contactDetails.contact_email || '';
            let buyerPhone = contactDetails.contact_number || '';
            
            // Fallback to RFQ data if available
            if ((!buyerEmail || !buyerPhone) && fetchedRfqData) {
              buyerEmail = buyerEmail || fetchedRfqData.buyer_email || fetchedRfqData.email || '';
              buyerPhone = buyerPhone || fetchedRfqData.buyer_phone || fetchedRfqData.phone || '';
            }
            
            // Additional fallback: check if contact_details is a string that needs parsing
            if ((!buyerEmail || !buyerPhone) && typeof buyerProfile.contact_details === 'string') {
              try {
                const parsedContactDetails = JSON.parse(buyerProfile.contact_details);
                buyerEmail = buyerEmail || parsedContactDetails.contact_email || '';
                buyerPhone = buyerPhone || parsedContactDetails.contact_number || '';
              } catch (e) {
                // Ignore parsing errors
              }
            }
            
            setBuyerDetails(prev => ({
              ...prev,
              companyName: prev?.companyName || buyerProfile.company_name || 'N/A',
              address: formattedAddress || prev?.address || 'N/A',
              contactPerson: contactDetails.contact_name || buyerProfile.designation || 'N/A',
              email: buyerEmail || 'N/A',
              phone: buyerPhone || 'N/A'
            }));
          }
        } catch (buyerProfileError) {
          console.error('Error fetching buyer company profile:', buyerProfileError);
          // Fallback to RFQ data if company profile fetch fails
          if (fetchedRfqData) {
            setBuyerDetails(prev => ({
              ...prev,
              email: prev?.email || fetchedRfqData.buyer_email || fetchedRfqData.email || 'N/A',
              phone: prev?.phone || fetchedRfqData.buyer_phone || fetchedRfqData.phone || 'N/A'
            }));
          }
        }
      } else if (fetchedRfqData) {
        // If no buyer_company_id, try to get from RFQ data
        setBuyerDetails(prev => ({
          ...prev,
          email: prev?.email || fetchedRfqData.buyer_email || fetchedRfqData.email || 'N/A',
          phone: prev?.phone || fetchedRfqData.buyer_phone || fetchedRfqData.phone || 'N/A'
        }));
      }

      // Fetch seller company profile for contact details
      if (po.seller_company_id) {
        try {
          const sellerProfileResponse = await CommonService.get(`/company/profile/${po.seller_company_id}`);
          if (sellerProfileResponse && sellerProfileResponse.success && sellerProfileResponse.data) {
            const sellerProfile = sellerProfileResponse.data;
            const contactDetails = sellerProfile.contact_details || {};
            
            setSellerDetails(prev => ({
              ...prev,
              companyName: prev?.companyName || sellerProfile.company_name || 'N/A',
              contactPerson: contactDetails.contact_name || sellerProfile.designation || fetchedQuotation?.seller_name || 'N/A',
              email: contactDetails.contact_email || sellerProfile.email || fetchedQuotation?.seller_email || 'N/A',
              phone: contactDetails.contact_number || sellerProfile.phone || sellerProfile.contact_phone || fetchedQuotation?.seller_phone || 'N/A'
            }));
          }
        } catch (sellerProfileError) {
          console.error('Error fetching seller company profile:', sellerProfileError);
          // Fallback to quotation data if available
          if (fetchedQuotation) {
            setSellerDetails(prev => ({
              ...prev,
              contactPerson: prev?.contactPerson || fetchedQuotation.seller_name || 'N/A',
              email: prev?.email || fetchedQuotation.seller_email || 'N/A',
              phone: prev?.phone || fetchedQuotation.seller_phone || 'N/A'
            }));
          }
        }
      }

    } catch (error) {
      console.error('Error fetching PO data:', error);
      toast.error(error?.response?.data?.message || 'Failed to load purchase order data');
      navigate('/seller-purchase-order-list');
    } finally {
      setLoading(false);
    }
  };
  
  const [responseData, setResponseData] = useState({
    decision: '',
    acceptanceDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    poAckNumber: '',
    estimatedDispatch: '',
    estimatedDelivery: '',
    acceptanceNotes: '',
    termsAccept: false,
    acknowledgement: false,
    rejectionReason: '',
    rejectionDetails: '',
    negotiationAreas: {
      pricing: false,
      quantity: false,
      delivery: false,
      payment: false,
      specs: false,
      other: false
    },
    negotiationNotes: ''
  });

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  // Collapse/Expand state for sections (initially collapsed)
  const [deliveryScheduleExpanded, setDeliveryScheduleExpanded] = useState(false);
  const [termsConditionsExpanded, setTermsConditionsExpanded] = useState(false);
  const [specialInstructionsExpanded, setSpecialInstructionsExpanded] = useState(false);
  const [relatedDocumentsExpanded, setRelatedDocumentsExpanded] = useState(false);
  

  const rejectionReasons = [
    'Insufficient stock / Inventory unavailable',
    'Pricing not acceptable',
    'Delivery timeline too short',
    'Product specifications do not match',
    'Payment terms not suitable',
    'Quality standards cannot be met',
    'Manufacturing capacity constraints',
    'Other (please specify in details)'
  ];



  const calculateTotals = () => {
    if (!lineItems || lineItems.length === 0) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        taxableAmount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        cess: 0,
        grandTotal: 0
      };
    }

    const subtotal = lineItems.reduce((sum, item) => {
      const unitPrice = parseFloat(item.unit_price || item.unitPrice || 0);
      const quantity = parseFloat(item.quantity || 0);
      const discount = parseFloat(item.discount || 0);
      const itemSubtotal = unitPrice * quantity;
      const discountAmount = itemSubtotal * (discount / 100);
      return sum + (itemSubtotal - discountAmount);
    }, 0);

    const totalDiscount = lineItems.reduce((sum, item) => {
      const unitPrice = parseFloat(item.unit_price || item.unitPrice || 0);
      const quantity = parseFloat(item.quantity || 0);
      const discount = parseFloat(item.discount || 0);
      const itemSubtotal = unitPrice * quantity;
      return sum + (itemSubtotal * (discount / 100));
    }, 0);

    const taxableAmount = subtotal;
    const tradeScenario = formData?.tradeScenario || poData?.trade_scenario || '';
    
    // Calculate CGST (intra-state only)
    const cgst = lineItems.reduce((sum, item) => {
      let cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount || 0);
      
      // If cgst_amount is 0 or missing, calculate from percentage
      if ((!cgstAmount || cgstAmount === 0) && (item.cgst || item.cgstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const cgstPercent = getCgstPercentage(item);
        if (cgstPercent > 0 && itemTaxable > 0) {
          cgstAmount = itemTaxable * (cgstPercent / 100);
        }
      }
      
      return sum + cgstAmount;
    }, 0);

    // Calculate SGST (intra-state only)
    const sgst = lineItems.reduce((sum, item) => {
      let sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount || 0);
      
      // If sgst_amount is 0 or missing, calculate from percentage
      if ((!sgstAmount || sgstAmount === 0) && (item.sgst || item.sgstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const sgstPercent = getSgstPercentage(item);
        if (sgstPercent > 0 && itemTaxable > 0) {
          sgstAmount = itemTaxable * (sgstPercent / 100);
        }
      }
      
      return sum + sgstAmount;
    }, 0);

    const igst = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.igst_amount || item.igstAmount || 0);
    }, 0);

    // Calculate CESS
    const cess = lineItems.reduce((sum, item) => {
      let cessAmount = parseFloat(item.cess_amount || item.cessAmount || 0);
      
      // If cess_amount is 0 or missing, calculate from percentage
      if ((!cessAmount || cessAmount === 0) && (item.cess || item.cessPercent)) {
        const cessPercent = parseFloat(item.cess || item.cessPercent || 0);
        
        // For import scenarios, CESS is calculated on assessable value
        if (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import')) {
          const assessableValue = parseFloat(item.assessable_value || item.assessableValue || 0);
          if (cessPercent > 0 && assessableValue > 0) {
            cessAmount = assessableValue * (cessPercent / 100);
          }
        } else {
          // For other scenarios, CESS is calculated on taxable amount (FOB value)
          const itemTaxable = getItemTaxable(item);
          if (cessPercent > 0 && itemTaxable > 0) {
            cessAmount = itemTaxable * (cessPercent / 100);
          }
        }
      }
      
      return sum + cessAmount;
    }, 0);

    // Calculate Basic Customs Duty (BCD) for imports
    const basicCustomsDuty = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.bcd_amount || item.bcdAmount || 0);
    }, 0);

    // Calculate Tax for Third Country trades
    const tax = lineItems.reduce((sum, item) => {
      const itemTaxable = getItemTaxable(item);
      const taxPercent = parseFloat(item.tax || item.taxPercent || 0);
      if (taxPercent > 0 && itemTaxable > 0) {
        return sum + (itemTaxable * (taxPercent / 100));
      }
      return sum + parseFloat(item.tax_amount || item.taxAmount || 0);
    }, 0);

    let grandTotal;
    if (isInternationalTrade(tradeScenario)) {
      if (tradeScenario.includes('Import')) {
        grandTotal = taxableAmount + basicCustomsDuty + igst + cess;
      } else if (tradeScenario === 'Third Country (Abroad → Abroad)') {
        grandTotal = taxableAmount + tax;
      } else {
      grandTotal = taxableAmount + igst + cess;
      }
    } else if (tradeScenario.includes('Inter-State')) {
      grandTotal = taxableAmount + igst + cess;
    } else {
      grandTotal = taxableAmount + cgst + sgst + cess;
    }

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cess,
      basicCustomsDuty,
      tax,
      grandTotal
    };
  };

  const formatCurrency = (amount) => {
    const currency = formData?.currency || 'INR';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Check if trade is international
  const isInternationalTrade = (scenario) => {
    return scenario && (
      scenario.includes('Import') || 
      scenario.includes('Export') || 
      scenario.includes('Third Country') ||
      scenario.includes('→IN') ||
      scenario.includes('IN→')
    );
  };

  // Helper to get item taxable amount
  const getItemTaxable = (item) => {
    const itemSubtotal = parseFloat(item.subtotal || item.quantity * (item.unit_price || item.unitPrice) || 0);
    const itemDiscount = parseFloat(item.discount_amount || item.discountAmount || 0);
    return parseFloat(item.fob_value || item.fobValue || (itemSubtotal - itemDiscount));
  };

  // Helper function to get CGST percentage, calculating from amount if percentage is 0
  const getCgstPercentage = (item) => {
    let cgst = parseFloat(item.cgst || item.cgstPercent || 0);
    
    // If CGST percentage is 0, try to calculate from amount or check legacy fields
    if (cgst === 0) {
      // First, try to calculate from cgst_amount
      const cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount || 0);
      // Calculate taxable amount (FOB value) properly
      const itemTaxable = getItemTaxable(item);
      
      if (itemTaxable > 0 && cgstAmount > 0) {
        cgst = (cgstAmount / itemTaxable) * 100;
      } else {
        // Legacy: Check if CGST is stored in igst field for intra-state trades
        const igst = parseFloat(item.igst || item.igstPercent || 0);
        const scenario = formData?.tradeScenario || poData?.trade_scenario || '';
        
        // Only use igst as cgst for intra-state trades
        if (igst > 0 && (scenario === 'IN Intra-State (Same State in India)' || scenario.includes('Intra-State'))) {
          cgst = igst;
        }
      }
    }
    
    return cgst;
  };

  // Helper function to get SGST percentage, calculating from amount if percentage is 0
  const getSgstPercentage = (item) => {
    let sgst = parseFloat(item.sgst || item.sgstPercent || 0);
    
    // If SGST percentage is 0 but we have sgst_amount, calculate percentage from amount
    if (sgst === 0 && (item.sgst_amount || item.sgstAmount)) {
      const sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount || 0);
      const itemTaxable = getItemTaxable(item);
      if (itemTaxable > 0 && sgstAmount > 0) {
        sgst = (sgstAmount / itemTaxable) * 100;
      }
    }
    
    return sgst;
  };

  const totals = calculateTotals();

  const handleAccept = async () => {
    if (!poData || !poData.id) {
      toast.error('Purchase order data not available');
      return;
    }

    if (!responseData.termsAccept || !responseData.acknowledgement) {
      toast.error('Please accept terms and acknowledge review');
      return;
    }

    if (!responseData.estimatedDispatch || !responseData.estimatedDelivery) {
      toast.error('Please provide estimated dispatch and delivery dates');
      return;
    }

    try {
      setProcessing(true);
      const poId = poData.id;

      const acceptData = {
        poAckNumber: responseData.poAckNumber || null,
        estimatedDispatch: responseData.estimatedDispatch,
        estimatedDelivery: responseData.estimatedDelivery,
        acceptanceNotes: responseData.acceptanceNotes || null,
        acceptanceAttachment: null, // Can be added later for file upload
        termsAccepted: responseData.termsAccept,
        acknowledgementReviewed: responseData.acknowledgement
      };

      const response = await CommonService.post(`/purchase-order/${poId}/accept`, acceptData);
      
      if (response && response.success) {
        toast.success('Purchase order accepted successfully!');
        navigate('/invoice-list');
    } else {
        throw new Error(response?.message || 'Failed to accept purchase order');
      }
    } catch (error) {
      console.error('Error accepting purchase order:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to accept purchase order');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!poData || !poData.id) {
      toast.error('Purchase order data not available');
      return;
    }

    if (!responseData.rejectionReason || !responseData.rejectionDetails) {
      toast.error('Please provide rejection reason and details');
      return;
    }

    try {
      setProcessing(true);
      const poId = poData.id;

      const rejectData = {
        rejectionReason: responseData.rejectionReason,
        rejectionDetails: responseData.rejectionDetails,
        rejectionAttachment: null // Can be added later for file upload
      };

      const response = await CommonService.post(`/purchase-order/${poId}/reject`, rejectData);
      
      if (response && response.success) {
        toast.success('Purchase order rejected successfully');
        navigate('/seller-purchase-order-list');
      } else {
        throw new Error(response?.message || 'Failed to reject purchase order');
      }
    } catch (error) {
      console.error('Error rejecting purchase order:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to reject purchase order');
    } finally {
      setProcessing(false);
    }
  };

  const handleNegotiate = async () => {
    if (!poData || !poData.id) {
      toast.error('Purchase order data not available');
      return;
    }

    if (!responseData.negotiationNotes || responseData.negotiationNotes.trim() === '') {
      toast.error('Please provide negotiation notes');
      return;
    }

    try {
      setProcessing(true);
      const poId = poData.id;

      const negotiationData = {
        negotiationAreas: responseData.negotiationAreas,
        negotiationNotes: responseData.negotiationNotes
      };

      const response = await CommonService.post(`/purchase-order/${poId}/negotiate`, negotiationData);
      
      if (response && response.success) {
        toast.success('Negotiation request submitted successfully!');
        navigate('/seller-purchase-order-list');
      } else {
        throw new Error(response?.message || 'Failed to submit negotiation request');
      }
    } catch (error) {
      console.error('Error submitting negotiation request:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to submit negotiation request');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (responseData.decision === 'accept') {
      handleAccept();
    } else if (responseData.decision === 'reject') {
      handleReject();
    } else if (responseData.decision === 'negotiate') {
      handleNegotiate();
    } else {
      toast.error('Please select a decision');
    }
  };

  if (loading || !formData) {
    return (
      <div className="po-accept">
        <div className="po-accept-main-content" style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading purchase order data...</p>
        </div>
      </div>
    );
  }

  const poRecordId = poData?.id != null ? String(poData.id) : null;

  return (
    <div className="po-accept">
      <div className="po-accept-main-content">
        {/* Header */}
        <div className="po-accept-header">
          <div className="po-accept-header-left">
            <nav aria-label="breadcrumb" className="po-accept-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a 
                    href="#" 
                    className="breadcrumb-link" 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(-1);
                    }}
                  >
                    Purchase Orders
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Accept Purchase Order
                </li>
              </ol>
            </nav>
            <h1 className="po-accept-main-title">Purchase Order: {formData?.poNumber || 'N/A'}</h1>
            <p className="po-accept-subtitle">
              Review and accept the purchase order from {buyerDetails?.companyName || 'N/A'}
            </p>
          </div>
          <div className="po-accept-header-right">
            <div className="po-accept-header-buttons">
              <button className="po-accept-back-btn" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                Back
              </button>
              <button className="po-accept-btn-download-po" onClick={() => {
                // Handle download PO
                console.log('Download PO clicked');
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Download PO
              </button>
              <button className="po-accept-btn-contact-buyer" onClick={() => {
                setShowContactModal(true);
                setContactFormData({
                  subject: `Regarding Purchase Order ${poData?.po_number || formData?.poNumber || 'N/A'}`,
                  message: '',
                  priority: 'normal'
                });
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Contact Buyer
              </button>
            </div>
            <div className="po-accept-trade-badge-wrapper">
              <span className="po-accept-badge">{formData?.tradeType || 'N/A'}</span>
              <p className="po-accept-trade-location">{(formData?.tradeLocation || '').replace(' -> ', ' ↔ ')}</p>
            </div>
          </div>
        </div>

        {/* Action Required Alert */}
        <div className="po-accept-alert po-accept-alert-warning" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-alert-icon" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" x2="12" y1="8" y2="12"></line>
            <line x1="12" x2="12.01" y1="16" y2="16"></line>
          </svg>
          <div className="po-accept-alert-description">
            <strong>Action Required:</strong> Please review this purchase order carefully and provide your response within 48 hours.
          </div>
        </div>

        {/* Reference Quotation Card */}
        <div className="po-reference-quotation-card">
          <div className="po-reference-quotation-header">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="po-reference-icon">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            <h3 className="po-section-title">Reference Quotation</h3>
          </div>
          <div className="po-reference-quotation-content">
            {/* First Row - 4 Columns */}
            <div className="po-reference-quotation-row po-reference-quotation-row-first">
              <div className="po-reference-quotation-item">
                <label>Quotation ID</label>
                <p className="po-reference-value">{quotationData?.quotation_number || poData?.quotation_number || 'N/A'}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Linked RFQ</label>
                <p className="po-reference-value">{poData?.rfq_id ? `REQ-${poData.rfq_id}` : (rfqData?.rfq_id ? `REQ-${rfqData.rfq_id}` : 'N/A')}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Quote Date</label>
                <p className="po-reference-value">{quotationData?.quote_date ? formatDate(quotationData.quote_date) : 'N/A'}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Valid Until</label>
                <p className="po-reference-value">
                  {(() => {
                    if (quotationData?.valid_until_date) {
                      return formatDate(quotationData.valid_until_date);
                    }
                    if (quotationData?.valid_until) {
                      return formatDate(quotationData.valid_until);
                    }
                    if (quotationData?.validity_date) {
                      return formatDate(quotationData.validity_date);
                    }
                    // Calculate from quote_date + validity_days
                    if (quotationData?.quote_date && quotationData?.validity_days) {
                      try {
                        const quoteDateObj = new Date(quotationData.quote_date);
                        quoteDateObj.setDate(quoteDateObj.getDate() + parseInt(quotationData.validity_days));
                        return formatDate(quoteDateObj);
                      } catch (e) {
                        console.error('Error calculating valid until date:', e);
                      }
                    }
                    return 'N/A';
                  })()}
                </p>
              </div>
            </div>
            
            {/* Horizontal Line */}
            <div className="po-reference-quotation-divider"></div>
            
            {/* Second Row - 4 Columns */}
            <div className="po-reference-quotation-row po-reference-quotation-row-second">
              <div className="po-reference-quotation-item">
                <label>Seller Company</label>
                <p className="po-reference-value">{sellerDetails?.companyName || 'N/A'}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Contact Person</label>
                <p className="po-reference-value">{sellerDetails?.contactPerson || 'N/A'}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Contact Email</label>
                <p className="po-reference-value">{sellerDetails?.email || 'N/A'}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Contact Phone</label>
                <p className="po-reference-value">{sellerDetails?.phone || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Order Details Card */}
        <div className="po-accept-purchase-order-details-section">
          <div className="po-accept-reference-quotation-header">
            <h4 className="po-accept-section-title">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-reference-icon" aria-hidden="true">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                <path d="M10 9H8"></path>
                <path d="M16 13H8"></path>
                <path d="M16 17H8"></path>
              </svg>
              Purchase Order Details
            </h4>
          </div>
          <div className="po-accept-purchase-order-details-content">
            <div className="po-accept-po-details-grid">
              <div className="po-accept-reference-quotation-item">
                <label>PO Number</label>
                <p className="po-accept-reference-value">{formData?.poNumber || 'N/A'}</p>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>PO Date</label>
                <p className="po-accept-reference-value">{formData?.poDate ? formatDate(formData.poDate) : 'N/A'}</p>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>Requested Delivery</label>
                <p className="po-accept-reference-value">{formData?.expectedDeliveryDate ? formatDate(formData.expectedDeliveryDate) : 'N/A'}</p>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>Priority</label>
                <span className="po-accept-badge po-accept-priority-badge">{formData?.priorityLevel || 'Normal'}</span>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>Reference Quotation</label>
                <p className="po-accept-reference-value">{quotationData?.quotation_number || poData?.quotation_number || 'N/A'}</p>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>Reference RFQ</label>
                <p className="po-accept-reference-value">{poData?.rfq_id ? `REQ-${poData.rfq_id}` : (rfqData?.rfq_id ? `REQ-${rfqData.rfq_id}` : 'N/A')}</p>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>Payment Terms</label>
                <p className="po-accept-reference-value">{formData?.paymentTerms || 'N/A'}</p>
              </div>
              <div className="po-accept-reference-quotation-item">
                <label>Total Amount</label>
                <p className="po-accept-reference-value po-accept-total-amount">{formatCurrency(totals.grandTotal)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Buyer Details Card */}
        <div className="po-accept-purchase-order-details-section">
          <div className="po-accept-reference-quotation-header">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-reference-icon">
              <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
              <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
              <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
              <path d="M10 6h4"></path>
              <path d="M10 10h4"></path>
              <path d="M10 14h4"></path>
              <path d="M10 18h4"></path>
            </svg>
            <h3 className="po-accept-section-title">Buyer Details</h3>
          </div>
          <div className="po-accept-purchase-order-details-content">
            <div className="po-accept-buyer-info-grid">
              <div className="po-accept-buyer-info-item">
                <label>Company Name</label>
                <p className="po-accept-buyer-info-value">{buyerDetails?.companyName || 'N/A'}</p>
              </div>
              <div className="po-accept-buyer-info-item">
                <label>Contact Person</label>
                <p className="po-accept-buyer-info-value">{buyerDetails?.contactPerson || 'N/A'}</p>
              </div>
              <div className="po-accept-buyer-info-item">
                <label>Email</label>
                <p className="po-accept-buyer-info-value">{buyerDetails?.email || 'N/A'}</p>
              </div>
              <div className="po-accept-buyer-info-item">
                <label>Phone</label>
                <p className="po-accept-buyer-info-value">{buyerDetails?.phone || 'N/A'}</p>
              </div>
              <div className="po-accept-buyer-info-item po-accept-buyer-info-item-full">
                <label>Delivery Address</label>
                <p className="po-accept-buyer-info-value">{buyerDetails?.address || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Ordered Items Card */}
        <div className="po-accept-line-items-card">
          <div className="po-accept-line-items-header">
            <div className="po-accept-line-items-header-left">
              <h4 className="po-accept-line-items-title">Ordered Items</h4>
             
            </div>
          </div>
          <div className="po-accept-line-items-content">
            <div className="po-accept-table-wrapper">
              <div className="po-accept-table-container">
                <table className="po-accept-line-items-table">
                  <thead>
                    <tr>
                      <th className="po-accept-table-col-number">#</th>
                      <th className="po-accept-table-col-description">Description</th>
                      <th>HSN</th>
                      <th className="po-accept-table-col-qty">Qty</th>
                      <th>UOM</th>
                      <th className="po-accept-table-col-price">Unit Price</th>
                      <th className="po-accept-table-col-disc">Disc %</th>
                      {isInternationalTrade(formData?.tradeScenario || poData?.trade_scenario || '') ? (
                        <>
                          {(formData?.tradeScenario || poData?.trade_scenario || '') === 'IN→ Export from India (India → Abroad)' ? (
                            <>
                              <th className="po-accept-table-col-tax">IGST %</th>
                              <th className="po-accept-table-col-tax">CESS %</th>
                            </>
                          ) : (formData?.tradeScenario || poData?.trade_scenario || '') === 'Third Country (Abroad → Abroad)' ? (
                            <>
                              <th className="po-accept-table-col-tax">Tax %</th>
                            </>
                          ) : (
                            <>
                              <th className="po-accept-table-col-tax">Customs %</th>
                              <th className="po-accept-table-col-tax">IGST %</th>
                              <th className="po-accept-table-col-tax">CESS %</th>
                            </>
                          )}
                        </>
                      ) : (formData?.tradeScenario || poData?.trade_scenario || '') === 'IN Inter-State (Between Indian States)' ? (
                        <>
                          <th className="po-accept-table-col-tax">IGST %</th>
                          <th className="po-accept-table-col-tax">CESS %</th>
                        </>
                      ) : (
                        <>
                          <th className="po-accept-table-col-tax">CGST %</th>
                          <th className="po-accept-table-col-tax">SGST %</th>
                          <th className="po-accept-table-col-tax">CESS %</th>
                        </>
                      )}
                      <th className="po-accept-table-col-total">Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => {
                      const tradeScenario = formData?.tradeScenario || poData?.trade_scenario || '';
                      const isInternational = isInternationalTrade(tradeScenario);
                      return (
                        <tr key={item.line_item_id || item.id || index}>
                        <td>{index + 1}</td>
                          <td>{item.description || 'N/A'}</td>
                          <td>{item.hsn_code || item.hsn || 'N/A'}</td>
                          <td>{item.quantity || 0}</td>
                          <td>{item.uom || 'N/A'}</td>
                          <td>{formatCurrency(item.unit_price || item.unitPrice || 0)}</td>
                          <td>{item.discount || 0}%</td>
                          {isInternational ? (
                            <>
                              {tradeScenario === 'IN→ Export from India (India → Abroad)' ? (
                                <>
                                  <td>{item.igst || 0}%</td>
                                  <td>{item.cess || item.cessPercent || 0}%</td>
                                </>
                              ) : tradeScenario === 'Third Country (Abroad → Abroad)' ? (
                                <>
                                  <td>{item.tax || item.taxPercent || 0}%</td>
                          </>
                        ) : (
                          <>
                                  <td>{item.customs || item.customsDuty || item.bcd || 0}%</td>
                                  <td>{item.igst || 0}%</td>
                                  <td>{item.cess || item.cessPercent || 0}%</td>
                          </>
                        )}
                            </>
                          ) : tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                            <>
                              <td>{item.igst || 0}%</td>
                              <td>{item.cess || item.cessPercent || 0}%</td>
                            </>
                          ) : (
                            <>
                              <td>{getCgstPercentage(item)}%</td>
                              <td>{getSgstPercentage(item)}%</td>
                              <td>{item.cess || item.cessPercent || 0}%</td>
                            </>
                          )}
                          <td className="po-accept-table-col-total-cell">{formatCurrency(item.total || 0)}</td>
                      </tr>
                      );
                    })}
                    {/* Summary Rows */}
                    {(() => {
                      const tradeScenario = formData?.tradeScenario || poData?.trade_scenario || '';
                      const isInternational = isInternationalTrade(tradeScenario);
                      
                      // Calculate colSpan based on trade scenario
                      let colSpan;
                      if (isInternational) {
                        if (tradeScenario === 'IN→ Export from India (India → Abroad)') {
                          colSpan = "8"; // #, Description, HSN, Qty, UOM, Unit Price, Disc %, IGST %, CESS %
                        } else if (tradeScenario === 'Third Country (Abroad → Abroad)') {
                          colSpan = "7"; // #, Description, HSN, Qty, UOM, Unit Price, Disc %, Tax %
                        } else {
                          colSpan = "9"; // #, Description, HSN, Qty, UOM, Unit Price, Disc %, Customs %, IGST %, CESS %
                        }
                      } else if (tradeScenario === 'IN Inter-State (Between Indian States)') {
                        colSpan = "8"; // #, Description, HSN, Qty, UOM, Unit Price, Disc %, IGST %, CESS %
                      } else {
                        colSpan = "9"; // #, Description, HSN, Qty, UOM, Unit Price, Disc %, CGST %, SGST %, CESS %
                      }
                      
                      return (
                        <>
                    <tr className="po-accept-table-summary-row">
                      <td></td>
                            <td colSpan={colSpan} className="po-accept-table-summary-label">Subtotal:</td>
                      <td className="po-accept-table-summary-value">{formatCurrency(totals.subtotal)}</td>
                    </tr>
                    <tr className="po-accept-table-summary-row">
                      <td></td>
                            <td colSpan={colSpan} className="po-accept-table-summary-label">Total Discount:</td>
                      <td className="po-accept-table-summary-value">- {formatCurrency(totals.totalDiscount)}</td>
                    </tr>
                    <tr className="po-accept-table-summary-row">
                      <td></td>
                            <td colSpan={colSpan} className="po-accept-table-summary-label">Taxable Amount:</td>
                      <td className="po-accept-table-summary-value">{formatCurrency(totals.taxableAmount)}</td>
                    </tr>
                          {isInternational ? (
                            <>
                              {tradeScenario === 'IN→ Export from India (India → Abroad)' ? (
                      <>
                        <tr className="po-accept-table-summary-row">
                          <td></td>
                                    <td colSpan={colSpan} className="po-accept-table-summary-label">IGST:</td>
                          <td className="po-accept-table-summary-value">{formatCurrency(totals.igst)}</td>
                        </tr>
                        <tr className="po-accept-table-summary-row">
                          <td></td>
                                    <td colSpan={colSpan} className="po-accept-table-summary-label">CESS:</td>
                          <td className="po-accept-table-summary-value">{formatCurrency(totals.cess)}</td>
                        </tr>
                      </>
                              ) : tradeScenario === 'Third Country (Abroad → Abroad)' ? (
                                <>
                                  <tr className="po-accept-table-summary-row">
                                    <td></td>
                                    <td colSpan={colSpan} className="po-accept-table-summary-label">Tax:</td>
                                    <td className="po-accept-table-summary-value">{formatCurrency(totals.tax || 0)}</td>
                                  </tr>
                                </>
                    ) : (
                      <>
                        <tr className="po-accept-table-summary-row">
                          <td></td>
                                    <td colSpan={colSpan} className="po-accept-table-summary-label">Customs Duty:</td>
                                    <td className="po-accept-table-summary-value">{formatCurrency(totals.basicCustomsDuty || 0)}</td>
                                  </tr>
                                  <tr className="po-accept-table-summary-row">
                                    <td></td>
                                    <td colSpan={colSpan} className="po-accept-table-summary-label">IGST:</td>
                                    <td className="po-accept-table-summary-value">{formatCurrency(totals.igst)}</td>
                                  </tr>
                                  <tr className="po-accept-table-summary-row">
                                    <td></td>
                                    <td colSpan={colSpan} className="po-accept-table-summary-label">CESS:</td>
                                    <td className="po-accept-table-summary-value">{formatCurrency(totals.cess)}</td>
                                  </tr>
                                </>
                              )}
                            </>
                          ) : tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                            <>
                              <tr className="po-accept-table-summary-row">
                                <td></td>
                                <td colSpan={colSpan} className="po-accept-table-summary-label">IGST:</td>
                                <td className="po-accept-table-summary-value">{formatCurrency(totals.igst)}</td>
                              </tr>
                              <tr className="po-accept-table-summary-row">
                                <td></td>
                                <td colSpan={colSpan} className="po-accept-table-summary-label">CESS:</td>
                                <td className="po-accept-table-summary-value">{formatCurrency(totals.cess)}</td>
                              </tr>
                            </>
                          ) : (
                            <>
                              <tr className="po-accept-table-summary-row">
                                <td></td>
                                <td colSpan={colSpan} className="po-accept-table-summary-label">CGST:</td>
                          <td className="po-accept-table-summary-value">{formatCurrency(totals.cgst)}</td>
                        </tr>
                        <tr className="po-accept-table-summary-row">
                          <td></td>
                                <td colSpan={colSpan} className="po-accept-table-summary-label">SGST:</td>
                          <td className="po-accept-table-summary-value">{formatCurrency(totals.sgst)}</td>
                        </tr>
                        <tr className="po-accept-table-summary-row">
                          <td></td>
                                <td colSpan={colSpan} className="po-accept-table-summary-label">CESS:</td>
                          <td className="po-accept-table-summary-value">{formatCurrency(totals.cess)}</td>
                        </tr>
                      </>
                    )}
                    <tr className="po-accept-table-summary-row po-accept-table-grand-total">
                      <td></td>
                            <td colSpan={colSpan} className="po-accept-table-summary-label">
                        <strong>Grand Total:</strong>
                      </td>
                      <td className="po-accept-table-summary-value">
                        <strong>{formatCurrency(totals.grandTotal)}</strong>
                      </td>
                    </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Schedule Card */}
        <div className="po-accept-line-items-card">
          <div 
            className="po-accept-line-items-header"
            onClick={() => setDeliveryScheduleExpanded(!deliveryScheduleExpanded)}
          >
            <div className="po-accept-line-items-header-left">
              <h4 className="po-accept-line-items-title">Delivery Schedule</h4>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className={`po-accept-chevron ${deliveryScheduleExpanded ? 'po-accept-chevron-expanded' : 'po-accept-chevron-collapsed'}`}
              aria-hidden="true"
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {deliveryScheduleExpanded && (
          <div className="po-accept-line-items-content">
            <div className="po-accept-table-wrapper">
              <div className="po-accept-table-container">
                <table className="po-accept-line-items-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Quantity</th>
                      <th>Delivery Date</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliverySchedules && deliverySchedules.length > 0 ? (
                      deliverySchedules.map((schedule, index) => {
                        const selectedItem = lineItems.find(item => 
                          (item.line_item_id || item.id || '').toString() === (schedule.item || schedule.itemId || '').toString()
                        );
                      return (
                          <tr key={schedule.id || schedule.deliveryScheduleId || index}>
                        <td>
                              {selectedItem ? (selectedItem.description || 'N/A') : (schedule.itemDescription || 'N/A')}
                        </td>
                        <td>
                          {schedule.quantity || 0}
                        </td>
                        <td>
                              {schedule.deliveryDate ? formatDate(schedule.deliveryDate) : 'N/A'}
                        </td>
                        <td>
                              {schedule.location || schedule.deliveryLocation || 'N/A'}
                        </td>
                      </tr>
                      );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '1rem' }}>No delivery schedule available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Terms & Conditions Card */}
        <div className="po-accept-line-items-card">
          <div 
            className="po-accept-line-items-header"
            onClick={() => setTermsConditionsExpanded(!termsConditionsExpanded)}
          >
            <div className="po-accept-line-items-header-left">
              <h4 className="po-accept-line-items-title">Terms & Conditions</h4>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className={`po-accept-chevron ${termsConditionsExpanded ? 'po-accept-chevron-expanded' : 'po-accept-chevron-collapsed'}`}
              aria-hidden="true"
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {termsConditionsExpanded && (
          <div className="po-accept-line-items-content">
            <div className="po-accept-terms-list">
              {termsConditions && termsConditions.length > 0 ? (
                termsConditions.map((term, index) => (
                  <div key={term.id || term.termId || index} className="po-accept-term-item">
                  <span className="po-accept-term-number">{index + 1}.</span>
                    <p className="po-accept-term-text">{term.text || term.termText || term}</p>
                </div>
                ))
              ) : (
                <p style={{ padding: '1rem', color: '#666' }}>No terms and conditions specified</p>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Special Instructions Card */}
        <div className="po-accept-line-items-card">
          <div 
            className="po-accept-line-items-header"
            onClick={() => setSpecialInstructionsExpanded(!specialInstructionsExpanded)}
          >
            <div className="po-accept-line-items-header-left">
              <h4 className="po-accept-line-items-title">Special Instructions</h4>
            </div>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className={`po-accept-chevron ${specialInstructionsExpanded ? 'po-accept-chevron-expanded' : 'po-accept-chevron-collapsed'}`}
              aria-hidden="true"
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {specialInstructionsExpanded && (
          <div className="po-accept-line-items-content">
            <textarea 
              className="po-accept-special-instructions-textarea" 
              placeholder="Any special packaging, labeling, or delivery instructions..."
              value={specialInstructions}
              readOnly
            />
          </div>
          )}
        </div>

        {/* Related Documents Card */}
        <div className="po-status-card po-status-card-full-width">
          <div 
            className="po-status-card-header"
            onClick={() => setRelatedDocumentsExpanded(!relatedDocumentsExpanded)}
          >
            <h4 className="po-status-card-title po-status-card-title-with-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                <path d="M10 9H8"></path>
                <path d="M16 13H8"></path>
                <path d="M16 17H8"></path>
              </svg>
              Related Documents
            </h4>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className={`po-accept-chevron ${relatedDocumentsExpanded ? 'po-accept-chevron-expanded' : 'po-accept-chevron-collapsed'}`}
              aria-hidden="true"
            >
              <path d="m18 15-6-6-6 6"></path>
            </svg>
          </div>
          {relatedDocumentsExpanded && (
          <div className="po-status-documents-content">
            {attachments && attachments.length > 0 ? (
            <div className="po-status-documents-grid">
                {attachments.map((attachment, index) => {
                  const fileName = attachment.name || attachment.fileName || attachment;
                  const filePath = attachment.path || attachment.filePath || attachment;
                  
                  const handleViewAttachment = async () => {
                    if (!poData || !poData.id) {
                      toast.error('Purchase order data not available');
                      return;
                    }
                    try {
                      const poId = poData.id;
                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                      const response = await fetch(`${API_BASE_URL}/purchase-order/${poId}/attachment/${encodeURIComponent(fileName)}`, {
                        method: 'GET',
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to view attachment');
                      }
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      window.open(url, '_blank');
                    } catch (error) {
                      console.error('Error viewing attachment:', error);
                      toast.error('Failed to view attachment');
                    }
                  };
                  
                  const handleDownloadAttachment = async () => {
                    if (!poData || !poData.id) {
                      toast.error('Purchase order data not available');
                      return;
                    }
                    try {
                      const poId = poData.id;
                      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                      const response = await fetch(`${API_BASE_URL}/purchase-order/${poId}/attachment/${encodeURIComponent(fileName)}`, {
                        method: 'GET',
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to download attachment');
                      }
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = fileName;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('Attachment downloaded successfully');
                    } catch (error) {
                      console.error('Error downloading attachment:', error);
                      toast.error('Failed to download attachment');
                    }
                  };
                  
                  return (
                    <div key={index} className="po-status-attachment-item">
                      <div className="po-status-attachment-info">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                  <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                </svg>
                        <span className="po-status-attachment-name">{fileName}</span>
                      </div>
                      <div className="po-status-attachment-actions">
                        <button className="po-status-attachment-btn" onClick={handleViewAttachment} title="View">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
              </button>
                        <button className="po-status-attachment-btn" onClick={handleDownloadAttachment} title="Download">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </button>
            </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ padding: '1rem', color: '#666', textAlign: 'center' }}>No documents available</p>
            )}
          </div>
          )}
        </div>

        {/* Your Response Card */}
        <div className="po-accept-response-card">
          <div className="po-accept-response-header">
            <h4 className="po-accept-response-title">Your Response</h4>
            <p className="po-accept-response-description">
              Accept, reject, or request modifications to this purchase order
            </p>
          </div>
          <div className="po-accept-response-content">
            {/* Decision Radio Group */}
            <div className="po-accept-response-field">
              <label className="po-accept-response-label">
                Decision *
              </label>
              <div className="po-accept-radio-group">
                <div 
                  className={`po-accept-radio-option ${responseData.decision === 'accept' ? 'po-accept-radio-selected' : ''}`}
                  onClick={() => setResponseData({...responseData, decision: 'accept'})}
                >
                  <button 
                    type="button"
                    role="radio"
                    aria-checked={responseData.decision === 'accept'}
                    className="po-accept-radio-button"
                  >
                    {responseData.decision === 'accept' && (
                      <span className="po-accept-radio-indicator">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-radio-dot">
                          <circle cx="12" cy="12" r="10"></circle>
                        </svg>
                      </span>
                    )}
                  </button>
                  <label className="po-accept-radio-label" htmlFor="accept">
                    <div className="po-accept-radio-content">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-radio-icon po-accept-icon-green">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                      <div>
                        <p>Accept Purchase Order</p>
                        <p className="po-accept-radio-subtitle">Confirm you can fulfill this order as specified</p>
                      </div>
                    </div>
                  </label>
                </div>
                
                <div 
                  className={`po-accept-radio-option po-accept-radio-option-reject ${responseData.decision === 'reject' ? 'po-accept-radio-selected' : ''}`}
                  onClick={() => setResponseData({...responseData, decision: 'reject'})}
                >
                  <button 
                    type="button"
                    role="radio"
                    aria-checked={responseData.decision === 'reject'}
                    className="po-accept-radio-button"
                  >
                    {responseData.decision === 'reject' && (
                      <span className="po-accept-radio-indicator">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-radio-dot">
                          <circle cx="12" cy="12" r="10"></circle>
                        </svg>
                      </span>
                    )}
                  </button>
                  <label className="po-accept-radio-label" htmlFor="reject">
                    <div className="po-accept-radio-content">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-radio-icon po-accept-icon-red">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m15 9-6 6"></path>
                        <path d="m9 9 6 6"></path>
                      </svg>
                      <div>
                        <p>Reject Purchase Order</p>
                        <p className="po-accept-radio-subtitle">Unable to fulfill this order</p>
                      </div>
                    </div>
                  </label>
                </div>
                
                <div 
                  className={`po-accept-radio-option po-accept-radio-option-negotiate ${responseData.decision === 'negotiate' ? 'po-accept-radio-selected' : ''}`}
                  onClick={() => setResponseData({...responseData, decision: 'negotiate'})}
                >
                  <button 
                    type="button"
                    role="radio"
                    aria-checked={responseData.decision === 'negotiate'}
                    className="po-accept-radio-button"
                  >
                    {responseData.decision === 'negotiate' && (
                      <span className="po-accept-radio-indicator">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-radio-dot">
                          <circle cx="12" cy="12" r="10"></circle>
                        </svg>
                      </span>
                    )}
                  </button>
                  <label className="po-accept-radio-label" htmlFor="negotiate">
                    <div className="po-accept-radio-content">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-accept-radio-icon po-accept-icon-yellow">
                        <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                      </svg>
                      <div>
                        <p>Request Negotiation</p>
                        <p className="po-accept-radio-subtitle">Propose changes to terms, quantities, or delivery</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* PO Acceptance Details */}
            {responseData.decision === 'accept' && (
              <div className="po-accept-acceptance-details">
                <h3 className="po-accept-acceptance-title">PO Acceptance Details</h3>
                <div className="po-accept-acceptance-grid">
                  <div className="po-accept-acceptance-field">
                    <label className="po-accept-response-label" htmlFor="acceptanceDate">
                      Acceptance Date *
                    </label>
                    <input 
                      type="text" 
                      id="acceptanceDate"
                      className="po-accept-input"
                      value={responseData.acceptanceDate}
                      readOnly
                    />
                  </div>
                  <div className="po-accept-acceptance-field">
                    <label className="po-accept-response-label" htmlFor="poAckNumber">
                      PO Acknowledgement Number
                    </label>
                    <input 
                      type="text" 
                      id="poAckNumber"
                      className="po-accept-input"
                      placeholder="e.g., ACK-2025-001"
                      value={responseData.poAckNumber}
                      onChange={(e) => setResponseData({...responseData, poAckNumber: e.target.value})}
                    />
                  </div>
                  <div className="po-accept-acceptance-field">
                    <label className="po-accept-response-label" htmlFor="estimatedDispatch">
                      Estimated Dispatch Date *
                    </label>
                    <input 
                      type="date"
                      id="estimatedDispatch"
                      className="po-accept-input po-accept-input-date"
                      value={responseData.estimatedDispatch}
                      onChange={(e) => setResponseData({...responseData, estimatedDispatch: e.target.value})}
                    />
                  </div>
                  <div className="po-accept-acceptance-field">
                    <label className="po-accept-response-label" htmlFor="estimatedDelivery">
                      Estimated Delivery Date *
                    </label>
                    <input 
                      type="date"
                      id="estimatedDelivery"
                      className="po-accept-input po-accept-input-date"
                      value={responseData.estimatedDelivery}
                      onChange={(e) => setResponseData({...responseData, estimatedDelivery: e.target.value})}
                    />
                  </div>
                </div>
                <div className="po-accept-acceptance-field po-accept-acceptance-field-full">
                  <label className="po-accept-response-label" htmlFor="acceptanceNotes">
                    Additional Notes (Optional)
                  </label>
                  <textarea 
                    id="acceptanceNotes"
                    className="po-accept-textarea"
                    placeholder="Any additional information for the buyer..."
                    rows="3"
                    value={responseData.acceptanceNotes}
                    onChange={(e) => setResponseData({...responseData, acceptanceNotes: e.target.value})}
                  ></textarea>
                </div>
                <div className="po-accept-checkbox-group">
                  <div className="po-accept-checkbox-item">
                    <input 
                      type="checkbox" 
                      id="termsAccept"
                      className="po-accept-checkbox"
                      checked={responseData.termsAccept}
                      onChange={(e) => setResponseData({...responseData, termsAccept: e.target.checked})}
                    />
                    <label htmlFor="termsAccept" className="po-accept-checkbox-label">
                      I accept all terms and conditions mentioned in the PO and confirm our ability to deliver as specified
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Rejection Details */}
            {responseData.decision === 'reject' && (
              <div className="po-accept-rejection-details">
                <h3 className="po-accept-rejection-title">Rejection Details</h3>
                <div className="po-accept-rejection-field">
                  <label className="po-accept-response-label" htmlFor="rejectionReason">
                    Reason for Rejection *
                  </label>
                  <select
                    id="rejectionReason"
                    className="po-accept-select"
                    value={responseData.rejectionReason}
                    onChange={(e) => setResponseData({...responseData, rejectionReason: e.target.value})}
                  >
                    <option value="">Select reason</option>
                    {rejectionReasons.map((reason, index) => (
                      <option key={index} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="po-accept-rejection-field">
                  <label className="po-accept-response-label" htmlFor="rejectionDetails">
                    Detailed Explanation *
                  </label>
                  <textarea 
                    id="rejectionDetails"
                    className="po-accept-textarea"
                    placeholder="Please provide detailed reason for rejection..."
                    rows="3"
                    value={responseData.rejectionDetails}
                    onChange={(e) => setResponseData({...responseData, rejectionDetails: e.target.value})}
                  ></textarea>
                </div>
              </div>
            )}

            {/* Negotiation Request Details */}
            {responseData.decision === 'negotiate' && (
              <div className="po-accept-negotiation-details">
                <h3 className="po-accept-negotiation-title">Negotiation Request</h3>
                <div className="po-accept-negotiation-field">
                  <label className="po-accept-response-label" htmlFor="negotiationArea">
                    What would you like to negotiate?
                  </label>
                  <div className="po-accept-negotiation-checkboxes">
                    <div className="po-accept-negotiation-checkbox-item">
                      <input 
                        type="checkbox" 
                        id="nego-price"
                        className="po-accept-checkbox"
                        checked={responseData.negotiationAreas.pricing}
                        onChange={(e) => setResponseData({
                          ...responseData, 
                          negotiationAreas: {...responseData.negotiationAreas, pricing: e.target.checked}
                        })}
                      />
                      <label htmlFor="nego-price" className="po-accept-checkbox-label">
                        Pricing
                      </label>
                    </div>
                    <div className="po-accept-negotiation-checkbox-item">
                      <input 
                        type="checkbox" 
                        id="nego-quantity"
                        className="po-accept-checkbox"
                        checked={responseData.negotiationAreas.quantity}
                        onChange={(e) => setResponseData({
                          ...responseData, 
                          negotiationAreas: {...responseData.negotiationAreas, quantity: e.target.checked}
                        })}
                      />
                      <label htmlFor="nego-quantity" className="po-accept-checkbox-label">
                        Quantities
                      </label>
                    </div>
                    <div className="po-accept-negotiation-checkbox-item">
                      <input 
                        type="checkbox" 
                        id="nego-delivery"
                        className="po-accept-checkbox"
                        checked={responseData.negotiationAreas.delivery}
                        onChange={(e) => setResponseData({
                          ...responseData, 
                          negotiationAreas: {...responseData.negotiationAreas, delivery: e.target.checked}
                        })}
                      />
                      <label htmlFor="nego-delivery" className="po-accept-checkbox-label">
                        Delivery Schedule
                      </label>
                    </div>
                    <div className="po-accept-negotiation-checkbox-item">
                      <input 
                        type="checkbox" 
                        id="nego-payment"
                        className="po-accept-checkbox"
                        checked={responseData.negotiationAreas.payment}
                        onChange={(e) => setResponseData({
                          ...responseData, 
                          negotiationAreas: {...responseData.negotiationAreas, payment: e.target.checked}
                        })}
                      />
                      <label htmlFor="nego-payment" className="po-accept-checkbox-label">
                        Payment Terms
                      </label>
                    </div>
                    <div className="po-accept-negotiation-checkbox-item">
                      <input 
                        type="checkbox" 
                        id="nego-specs"
                        className="po-accept-checkbox"
                        checked={responseData.negotiationAreas.specs}
                        onChange={(e) => setResponseData({
                          ...responseData, 
                          negotiationAreas: {...responseData.negotiationAreas, specs: e.target.checked}
                        })}
                      />
                      <label htmlFor="nego-specs" className="po-accept-checkbox-label">
                        Specifications
                      </label>
                    </div>
                    <div className="po-accept-negotiation-checkbox-item">
                      <input 
                        type="checkbox" 
                        id="nego-other"
                        className="po-accept-checkbox"
                        checked={responseData.negotiationAreas.other}
                        onChange={(e) => setResponseData({
                          ...responseData, 
                          negotiationAreas: {...responseData.negotiationAreas, other: e.target.checked}
                        })}
                      />
                      <label htmlFor="nego-other" className="po-accept-checkbox-label">
                        Other Terms
                      </label>
                    </div>
                  </div>
                </div>
                <div className="po-accept-negotiation-field">
                  <label className="po-accept-response-label" htmlFor="negotiationNotes">
                    Proposed Changes / Discussion Points *
                  </label>
                  <textarea 
                    id="negotiationNotes"
                    className="po-accept-textarea"
                    placeholder="Explain what changes you're proposing and why..."
                    rows="3"
                    value={responseData.negotiationNotes}
                    onChange={(e) => setResponseData({...responseData, negotiationNotes: e.target.value})}
                  ></textarea>
                </div>
              </div>
            )}

            {/* Acknowledgement Checkbox - Always Visible */}
            <div className="po-accept-checkbox-group">
              <div className="po-accept-checkbox-item">
                <input 
                  type="checkbox" 
                  id="acknowledgement"
                  className="po-accept-checkbox"
                  checked={responseData.acknowledgement}
                  onChange={(e) => setResponseData({...responseData, acknowledgement: e.target.checked})}
                />
                <label htmlFor="acknowledgement" className="po-accept-checkbox-label">
                  I acknowledge that I have reviewed all PO details including items, quantities, pricing, and terms
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="po-accept-response-actions">
              <button 
                className="po-accept-btn-primary" 
                onClick={handleSubmit}
                disabled={
                  processing ||
                  !responseData.decision || 
                  (responseData.decision === 'accept' && (!responseData.termsAccept || !responseData.acknowledgement || !responseData.estimatedDispatch || !responseData.estimatedDelivery)) ||
                  (responseData.decision === 'reject' && (!responseData.rejectionReason || !responseData.rejectionDetails)) ||
                  (responseData.decision === 'negotiate' && (!responseData.negotiationNotes || responseData.negotiationNotes.trim() === ''))
                }
              >
                {processing ? 'Processing...' :
                 responseData.decision === 'accept' ? 'Submit Acceptance' : 
                 responseData.decision === 'reject' ? 'Submit Rejection' : 
                 responseData.decision === 'negotiate' ? 'Submit Negotiation Request' :
                 'Select a Response'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Buyer Modal */}
      {showContactModal && (
        <div className="po-accept-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowContactModal(false);
          }
        }}>
          <div className="po-accept-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="po-accept-modal-header">
              <div className="po-accept-modal-header-left">
                <div className="po-accept-modal-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="po-accept-modal-title">Contact Buyer</h3>
                  <p className="po-accept-modal-subtitle">Send a message to {buyerDetails?.contactPerson || 'buyer'} at {buyerDetails?.companyName || 'N/A'}</p>
                </div>
              </div>
              <button 
                className="po-accept-modal-close" 
                onClick={() => setShowContactModal(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="po-accept-modal-body">
              {/* Buyer Info */}
              <div className="po-accept-contact-buyer-info">
                <div className="po-accept-contact-buyer-row po-accept-contact-buyer-row-first">
                  <div className="po-accept-contact-buyer-item">
                    <label className="po-accept-contact-buyer-label">To:</label>
                    <p className="po-accept-contact-buyer-value">{buyerDetails?.contactPerson || 'N/A'}</p>
                  </div>
                  <div className="po-accept-contact-buyer-item">
                    <label className="po-accept-contact-buyer-label">Company:</label>
                    <p className="po-accept-contact-buyer-value">{buyerDetails?.companyName || 'N/A'}</p>
                  </div>
                  <div className="po-accept-contact-buyer-item">
                    <label className="po-accept-contact-buyer-label">PO Number:</label>
                    <p className="po-accept-contact-buyer-value">{poData?.po_number || formData?.poNumber || 'N/A'}</p>
                  </div>
                </div>
                <div className="po-accept-contact-buyer-row po-accept-contact-buyer-row-second">
                  <div className="po-accept-contact-buyer-item">
                    <label className="po-accept-contact-buyer-label">Email:</label>
                    <p className="po-accept-contact-buyer-value">{buyerDetails?.email || 'N/A'}</p>
                  </div>
                  <div className="po-accept-contact-buyer-item">
                    <label className="po-accept-contact-buyer-label">Phone:</label>
                    <p className="po-accept-contact-buyer-value">{buyerDetails?.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="po-accept-contact-form">
                <div className="po-accept-contact-form-group">
                  <label className="po-accept-response-label" htmlFor="contactSubject">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="contactSubject"
                    className="input-control"
                    value={contactFormData.subject}
                    onChange={(e) => setContactFormData({...contactFormData, subject: e.target.value})}
                    placeholder="Enter message subject"
                  />
                </div>

                <div className="po-accept-contact-form-group">
                  <label className="po-accept-response-label" htmlFor="contactPriority">
                    Priority
                  </label>
                  <select
                    id="contactPriority"
                    className="input-control input-control-select"
                    value={contactFormData.priority}
                    onChange={(e) => setContactFormData({...contactFormData, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="po-accept-contact-form-group">
                  <label className="po-accept-response-label" htmlFor="contactMessage">
                    Message *
                  </label>
                  <textarea
                    id="contactMessage"
                    className="input-control"
                    rows="2"
                    value={contactFormData.message}
                    onChange={(e) => setContactFormData({...contactFormData, message: e.target.value})}
                    placeholder="Enter your message to the buyer..."
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="po-accept-modal-footer">
              <button 
                className="po-accept-modal-cancel-btn" 
                onClick={() => setShowContactModal(false)}
              >
                Cancel
              </button>
              <button 
                className="po-accept-modal-send-btn"
                onClick={async () => {
                  if (!contactFormData.subject || !contactFormData.message) {
                    toast.error('Please fill in all required fields');
                    return;
                  }

                  if (!poData || !poData.id) {
                    toast.error('Purchase order data not available');
                    return;
                  }

                  try {
                    setProcessing(true);
                    
                    // Get PO ID
                    let poId = poData.id;
                    if (typeof poId === 'string' && poId.startsWith('PO-')) {
                      const match = poId.match(/(\d+)$/);
                      if (match && match[1]) {
                        poId = parseInt(match[1], 10);
                      }
                    } else if (typeof poId === 'string') {
                      poId = parseInt(poId, 10);
                    }

                    // Format message with subject and priority
                    const fullMessage = `Subject: ${contactFormData.subject}\nPriority: ${contactFormData.priority}\n\n${contactFormData.message}`;

                    const response = await CommonService.post(
                      `/purchase-order/${poId}/send-message`,
                      { message: fullMessage }
                    );

                    if (response && response.success) {
                      toast.success('Message sent successfully!');
                    setShowContactModal(false);
                    // Reset form
                    setContactFormData({
                        subject: `Regarding Purchase Order ${formData?.poNumber || poData.po_number || 'N/A'}`,
                      message: '',
                      priority: 'normal'
                    });
                    } else {
                      throw new Error(response?.message || 'Failed to send message');
                    }
                  } catch (error) {
                    console.error('Error sending message:', error);
                    toast.error(error?.response?.data?.error || error?.message || 'Failed to send message');
                  } finally {
                    setProcessing(false);
                  }
                }}
                disabled={!contactFormData.subject || !contactFormData.message || processing}
              >
                {processing ? (
                  <>
                    <svg className="spinner" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

